import { PlanningActionMetadataDtoModelType, PlanningActionsStatus } from '../../../models';
import * as DuplicateRouteNotifications from '../../../../../notifications/routes/duplicateRoute';
import getAppEnv from '../../../utils/getAppEnv';
import getAppRoot from '../../../utils/getAppRoot';

type AppStore = ReturnType<typeof getAppRoot>;
type Model = AppStore['model'];
type UI = AppStore['ui'];
type AppEnv = ReturnType<typeof getAppEnv>;

const handleSameDateDuplication = async (model: Model, projectId: string, selectedDate: string | undefined) => {
  if (!selectedDate) return;
  await model.fetchProjectRoutes(projectId, selectedDate);
  model.fetchProjectRoutesExtraData(projectId, selectedDate);
};

const handleDifferentDateDuplication = async (model: Model, ui: UI, projectId: string) => {
  model.clearUnscheduledVisits();
  ui.setIsOrdersLoadingOnInit(true);
  await model.fetchUnscheduledVisitsLatLngs(projectId);
  ui.setIsOrdersLoadingOnInit(false);
};

const handlePostDuplicationDataRefresh = async (
  model: Model,
  ui: UI,
  planningAction: PlanningActionMetadataDtoModelType,
  selectedDate: string | undefined,
  projectId: string,
) => {
  if (selectedDate !== planningAction.startDate) {
    await handleDifferentDateDuplication(model, ui, projectId);
  } else {
    await handleSameDateDuplication(model, projectId, selectedDate);
  }
};

const handleFinishedStatus = async (
  appStore: AppStore,
  appEnv: AppEnv,
  fullPlanningAction: PlanningActionMetadataDtoModelType,
  selectedDate: string | undefined,
  projectId: string,
) => {
  const { model, ui } = appStore;

  const targetDate = fullPlanningAction.startDate;
  const count = ui.isDuplicatingRoute?.routeCount ?? 1;

  ui.setIsDuplicatingRoute(null);
  appEnv.closeMessage(DuplicateRouteNotifications.getDuplicatingMessageKey(projectId));

  await handlePostDuplicationDataRefresh(model, ui, fullPlanningAction, selectedDate, projectId);

  const navigateToTargetDate = async () => {
    if (!targetDate) return;

    ui.selectRoute(null);

    const currentSelectedDate = ui.selectedDate;

    if (currentSelectedDate !== targetDate) {

      ui.selectDate(targetDate);
    }
  };

  appEnv.notification(DuplicateRouteNotifications.duplicateRouteSuccessNotification(
    projectId,
    count,
    navigateToTargetDate,
    targetDate,
    selectedDate,
  ));

  appStore.planningActions.getUndoableRedoableActions(true);
};

export default (fullPlanningAction: PlanningActionMetadataDtoModelType) => async () => {
  const { store } = fullPlanningAction;
  const appStore = getAppRoot(store);
  const appEnv = getAppEnv(store);
  const { ui } = appStore;
  const maybeProjectId = ui.selectedWorkspace?.id;
  const { selectedDate } = ui;

  if (!maybeProjectId || maybeProjectId !== String(fullPlanningAction.projectId)) {
    return;
  }

  if (appStore.account?.user?.uuid !== fullPlanningAction.userUuid) {
    return;
  }

  const projectId: string = maybeProjectId;

  switch (fullPlanningAction.status) {
    case PlanningActionsStatus.NOT_STARTED:
    case PlanningActionsStatus.IN_PROGRESS: {
      const selectedRoute = ui.selectedRoute;
      const targetDate = fullPlanningAction.startDate ?? undefined;
      const routeCount = ui.isDuplicatingRoute?.routeCount ?? 1;

      ui.setIsDuplicatingRoute({
        targetDate,
        originalRouteUuid: selectedRoute?.uuid,
        routeCount,
      });

      appEnv.message(DuplicateRouteNotifications.duplicatingMessage(projectId, targetDate, routeCount));
      return;
    }
    case PlanningActionsStatus.FAILED:
    case PlanningActionsStatus.FINISHED_WITH_FAILED: {
      const errorMessage = fullPlanningAction.output?.error;
      const routeCount = ui.isDuplicatingRoute?.routeCount ?? 1;

      ui.setIsDuplicatingRoute(null);
      appEnv.closeMessage(DuplicateRouteNotifications.getDuplicatingMessageKey(projectId));
      appEnv.notification(DuplicateRouteNotifications.duplicateRouteFailureNotification(projectId, errorMessage, routeCount));
      break;
    }
    case PlanningActionsStatus.FINISHED: {
      await handleFinishedStatus(appStore, appEnv, fullPlanningAction, selectedDate, projectId);
      break;
    }
    default:
  }

  appStore.planningActions.getUndoableRedoableActions(true);
};
